import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  DropResult,
} from "react-beautiful-dnd";
import { toast } from "react-toastify";

import { SettingsCard } from "../../../../components/card/SettingsCard";
import { ColorPicker } from "../../../../components/colorPicker/ColorPicker";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import PlusSvg from "../../../../components/svg/PlusSvg";
import {
  BillingStatusType,
  useAddInvoiceStatusMutation,
  useDeleteInvoiceStatusMutation,
  useGetInvoiceStatusesQuery,
  useReorderInvoiceStatusMutation,
  useUpdateInvoiceStatusMutation,
} from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { SettingsFormInput } from "../../components/SettingsFormInput";
import { BillingStatus } from "../pages/BillingStatus";

type Reorder<T> = (list: T[], startIndex: number, endIndex: number) => T[];

const reorder: Reorder<BillingStatusType> = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const InvoicesStatuses = () => {
  const { data: statuses, isLoading } = useGetInvoiceStatusesQuery(
    getLinkedFirm()?.orgId || "",
  );

  const [data, setData] = useState<BillingStatusType[]>(statuses || []);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [newStatusData, setNewStatusData] = useState<{
    name: string;
    color: string;
  }>({ name: "", color: "#444444" });

  useEffect(() => {
    setData(statuses || []);
  }, [statuses]);

  const [
    addStatus,
    { isLoading: isLoadingAdd, isSuccess: isSuccessAdd, isError: isErrorAdd },
  ] = useAddInvoiceStatusMutation();

  const [
    updateStatus,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
    },
  ] = useUpdateInvoiceStatusMutation();

  const [reorderStatuses] = useReorderInvoiceStatusMutation();

  const [
    deleteStatus,
    {
      isLoading: isLoadingDelete,
      isSuccess: isSuccessDelete,
      isError: isErrorDelete,
    },
  ] = useDeleteInvoiceStatusMutation();

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newItems = reorder(
      statuses || [],
      result.source.index,
      result.destination.index,
    );

    setData(newItems);
    reorderStatuses(newItems.map((item) => item.id));
  };

  const handleUpdate = async (status: BillingStatusType) => {
    await updateStatus(
      statuses?.map((item) => (item.id === status.id ? status : item)),
    );
  };

  const handleAddStatus = async (value: string) => {
    await addStatus({
      name: value,
      order: (statuses?.length || 0) + 1,
      color: newStatusData.color,
      orgId: getLinkedFirm()?.orgId || "",
    });
  };

  const handleDelete = async (status: BillingStatusType) => {
    await deleteStatus(status.id || "");
  };

  useEffect(() => {
    if (isSuccessAdd || isSuccessUpdate || isSuccessDelete) {
      toast("Changes saved successfully", {
        type: "success",
        pauseOnHover: false,
      });
    }
  }, [isSuccessAdd, isSuccessUpdate, isSuccessDelete]);

  useEffect(() => {
    if (isErrorAdd || isErrorUpdate || isErrorDelete) {
      toast("Something went wrong", { type: "error", pauseOnHover: false });
    }
  }, [isErrorAdd, isErrorUpdate, isErrorDelete]);

  if (isLoading || isLoadingDelete || isLoadingAdd) {
    return <LottieLoading />;
  }

  return (
    <SettingsCard extraClasses={"w-full max-w-[500px]"}>
      <div className={"p-4 text-lg font-semibold"}>Invoice Labels</div>

      <div className={"mb-4 px-4"}>
        <button
          onClick={() => setIsOpenAdd(true)}
          className={
            "transitions-all flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-[#C8D2DE] bg-white px-4 text-sm font-semibold text-[#253747] duration-300 hover:border-[#798CA4] hover:bg-[#4C637B0D]"
          }>
          <PlusSvg />
          <span>Create Label</span>
        </button>
      </div>

      <div className={"rounded-b-xl border-t border-[#E4E9F2]"}>
        {isOpenAdd && (
          <div className={"border-b border-[#C8D2DE] py-2"}>
            <SettingsFormInput
              onSave={(value) => handleAddStatus(value)}
              onCancel={() => setIsOpenAdd(false)}
              placeholder={"Type label name..."}
              left={
                <ColorPicker
                  value={newStatusData.color}
                  onChange={(value) =>
                    setNewStatusData((prev) => ({ ...prev, color: value }))
                  }
                  preview={"Preview"}
                />
              }
            />
          </div>
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={"invoice-labels"}>
            {(provided: DroppableProvided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {data?.map((status, index) => (
                  <Draggable
                    key={status.id}
                    draggableId={status.id}
                    index={index}>
                    {(provided: DraggableProvided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={provided.draggableProps.style}>
                        <BillingStatus
                          status={status}
                          isLast={(data.length || 0) - 1 === index}
                          isUpdating={isLoadingUpdate}
                          handleUpdate={handleUpdate}
                          handleDelete={handleDelete}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </SettingsCard>
  );
};
